<template>
  <Provider>
    <component :is="layoutComponent"
               :class="[`theme-${themeName}`, `layout-${layoutComponentName}`, themeName, { 'opacity-0': loading }]">
      <RouterView v-slot="{ Component }">
        <transition :name="`router-${routerTransition}`" mode="out-in" appear>
          <component :is="Component" :key="forceRefresh"
                     :class="[`theme-${themeName}`, `layout-${layoutComponentName}`, themeName]"/>
        </transition>
      </RouterView>
    </component>
    <SplashScreen :show="loading"/>
    <SearchDialog v-if="isLogged"/>
    <BottomNavigation/>
  </Provider>
</template>
<script lang="ts" setup>
import {type Component, computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {useMainStore} from "@/stores/main"
import {useAuthStore} from "@/stores/auth"
import {useNotificationsStore} from "@/stores/useNotificationsStore"
import {useThemeStore} from "@/stores/theme"
import VerticalNav from "@/app-layouts/VerticalNav/index.vue"
import HorizontalNav from "@/app-layouts/HorizontalNav/index.vue"
import Blank from "@/app-layouts/Blank/index.vue"
import Provider from "@/app-layouts/common/Provider.vue"
import SplashScreen from "@/app-layouts/common/SplashScreen.vue"
import SearchDialog from "@/components/common/SearchDialog.vue"
import BottomNavigation from "@/components/common/BottomNavigation.vue"
import type {Layout, RouterTransition, ThemeName} from "@/types/theme.d"
import {type RouteLocationNormalized, useRoute, useRouter} from "vue-router"
import "@/assets/scss/index.scss"
import {emitter} from "@/emitter"
import {Capacitor} from '@capacitor/core'
import {PushNotifications} from '@capacitor/push-notifications';
import {Logger} from "@/utils"
import {useChatStore} from "@/stores/apps/useChatStore"


const router = useRouter()
const loading = ref(true)

const layoutComponents = {
  VerticalNav,
  HorizontalNav,
  Blank
}

const themeStore = useThemeStore()
const mainStore = useMainStore()
const authStore = useAuthStore()
const notifications = useNotificationsStore()
const route = useRoute()
const chatStore = useChatStore()

const forceRefresh = computed<number>(() => mainStore.forceRefresh)
const forceLayout = ref<Layout | null>(null)
const layout = computed<Layout>(() => themeStore.layout)
const layoutComponentName = computed<Layout>(() => forceLayout.value || layout.value)
const layoutComponent = computed<Component>(() => layoutComponents[layoutComponentName.value])
const routerTransition = computed<RouterTransition>(() => themeStore.routerTransition)
const themeName = computed<ThemeName>(() => themeStore.themeName)
const isLogged = computed(() => authStore.isLogged)

const addListeners = async () => {
  await PushNotifications.addListener('registration', token => {
    Logger.debug('Registration token: ', token.value)
  })

  await PushNotifications.addListener('registrationError', err => {
    Logger.debug('Registration error: ', err.error)
  })

  await PushNotifications.addListener('pushNotificationReceived', notification => {
    Logger.debug('Push notification received: ', typeof notification, notification)
  })

  await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
    let data = notification?.notification
    if (data?.data?.data) data.data = data.data.data
    Logger.debug('Remote Event (APS/FCM):', data)
    emitter.emit(`remoteevent:${data.data?.type}`, data)
  })
}

const registerNotifications = () => {
  return new Promise(async (resolve, reject) => {
    let isResolved = false
    let timeout = null as any

    try {
      let permStatus = await PushNotifications.checkPermissions()

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions()
      }

      if (permStatus.receive !== 'granted') {
        Logger.debug('User denied permissions!')
        throw new Error('User denied permissions!')
      }

      timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true
          new Error('Registration timeout')
        }
      }, 10000)

      let successListener: any = null
      let errorListener: any = null

      successListener = PushNotifications.addListener('registration', async token => {
        if (!isResolved) {
          isResolved = true
          clearTimeout(timeout)

          // Safely remove listeners
          if (successListener) {
            try {
              successListener.remove()
            } catch (e) {
              Logger.debug('Error removing success listener', e)
            }
          }
          if (errorListener) {
            try {
              errorListener.remove()
            } catch (e) {
              Logger.debug('Error removing error listener', e)
            }
          }

          authStore.setPushToken(token.value)
          resolve(token)
        }
      });

      errorListener = PushNotifications.addListener('registrationError', err => {
        if (!isResolved) {
          isResolved = true
          clearTimeout(timeout)

          // Safely remove listeners
          if (successListener) {
            try {
              successListener.remove()
            } catch (e) {
              Logger.debug('Error removing success listener', e)
            }
          }
          if (errorListener) {
            try {
              errorListener.remove()
            } catch (e) {
              Logger.debug('Error removing error listener', e)
            }
          }

          throw new Error(err.error)
        }
      })

      PushNotifications.register()

    } catch (e) {
      if (!isResolved) {
        isResolved = true
        clearTimeout(timeout)
        reject(e)
      }
    }
  })
}

const getDeliveredNotifications = async () => {
  const notificationList = await PushNotifications.getDeliveredNotifications();
  Logger.debug('delivered notifications', notificationList)
}


function checkForcedLayout(route: RouteLocationNormalized) {
  if (route.meta?.forceLayout) {
    forceLayout.value = route.meta.forceLayout
  } else {
    forceLayout.value = null
  }
}

watch(layoutComponentName, () => {
  loading.value = false
})

router.afterEach(route => {
  checkForcedLayout(route)
})


onMounted(() => {
  setTimeout(async () => {

    emitter.on('remoteevent:notification', data => {
      Logger.debug('Remote Event Notification:', data)
      const messageData = data?.data
      let title = data?.title || 'Notification'
      const subtitle = messageData?.title
      if (subtitle && title !== subtitle) title = `${title}: ${subtitle}`

      // category: "message" | "reminder" | "alert" | "news"
      // type: "success" | "info" | "warning" | "error" | "default"
      notifications.inject({
        title: title,
        description: data?.body,
        category: 'news',
        type: 'info',
        sendNotify: true,
        autoNotify: false
      })

      //notifications.fetchNotifications(7)
    })

    emitter.on('remoteevent:system', data => {
      Logger.debug('Remote Event System:', data)
      const messageData = data?.data
      let title = data?.title || 'System Notification'
      const subtitle = messageData?.title
      if (subtitle && title !== subtitle) title = `${title}: ${subtitle}`


      // category: "message" | "reminder" | "alert" | "news"
      // type: "success" | "info" | "warning" | "error" | "default"
      notifications.inject({
        title: title,
        description: data?.body,
        category: 'alert',
        type: 'warning',
        sendNotify: true,
        autoNotify: false
      })
    })

    emitter.on('remoteevent:chat', data => {
      Logger.debug('remote event chat:', data)

      if (route.name !== 'chat') {
        const messageData = data?.data
        const senderName = messageData?.sender?.name || 'Someone'
        const messageText = messageData?.sender?.bodytext || 'sent you a message'
        let title = data?.title || 'Chat Notification'
        const subtitle = messageData?.title
        if (subtitle && title !== subtitle) title = `${title}: ${subtitle}`

        // category: "message" | "reminder" | "alert" | "news"
        // type: "success" | "info" | "warning" | "error" | "default"
        notifications.inject({
          title: title,
          description: `${senderName}: ${messageText}`,
          category: 'message',
          type: 'info',
          actionTitle: 'Open Chat',
          action: async () => {
            const senderId = messageData?.sender?.id
            if (senderId) {
              await chatStore.startNewChat(senderId)
            }
            router.replace({name: 'Apps-Chat'})
          },
          sendNotify: true,
          autoNotify: false
        })
      }

      // entity.type === 'chat' means entity.id is a chat ID, not a message ID
      if (data?.data?.entity?.type === 'chatmessage') {
        chatStore.fetchNewMessage(data.data.entity.id)
      } else {
        chatStore.fetchMessages()
      }
    })

    emitter.on('remoteevent:chatmessage', data => {
      Logger.debug('Remote Event ChatMessage:', data)

      if (route.name !== 'chat') {
        const messageData = data?.data
        const senderName = messageData?.sender?.name || 'Someone'
        const messageText = messageData?.sender?.bodytext || 'sent you a message'
        let title = data?.title || 'Chat Notification'
        const subtitle = messageData?.title
        if (subtitle && title !== subtitle) title = `${title}: ${subtitle}`

        // category: "message" | "reminder" | "alert" | "news"
        // type: "success" | "info" | "warning" | "error" | "default"
        notifications.inject({
          title: title,
          description: `${senderName}: ${messageText}`,
          category: 'message',
          type: 'info',
          actionTitle: 'Open Chat',
          action: async () => {
            const senderId = messageData?.sender?.id
            if (senderId) {
              await chatStore.startNewChat(senderId)
            }
            router.replace({name: 'Apps-Chat'})
          },
          sendNotify: true,
          autoNotify: false
        })
      }

      if (data?.data?.entity?.type === 'chatmessage') {
        chatStore.fetchNewMessage(data.data.entity.id)
      } else {
        chatStore.fetchMessages()
      }
    })

    emitter.on('remoteevent:subscription', data => {

      const messageData = data?.data
      const subscription = messageData?.entity?.role || 'Unknown Subscription'

      // category: "message" | "reminder" | "alert" | "news"
      // type: "success" | "info" | "warning" | "error" | "default"
      notifications.inject({
        title: data?.title || 'Subscription Update',
        description: `${subscription}`,
        category: 'message',
        type: 'success',
        actionTitle: 'Open Profile',
        action: () => {
          router.replace({name: 'Profile'})
        },
        sendNotify: true,
        autoNotify: false
      })

      authStore.fetchAccountInfo()
    })


    authStore.fetchAccountInfo()
    notifications.initializeMercureSubscription()
    if (authStore.isLogged && authStore.webPushEndpoint && authStore.webPushEndpoint.length > 0) {
      notifications.missingWebPushSubscription().then((result) => {
        if (result.missing) {
          alert('You have a web push subscription but it is not active. Please logout and login again to activate it.')
        }
      })
    }
    notifications.fetchNotifications(7)

    if (Capacitor.isNativePlatform()) {
      try {
        Logger.debug('Capacitor is native running')

        await addListeners()
        registerNotifications()
            .then(token => {
              Logger.debug('Push notifications registered', token)
            })
            .catch(e => {
              Logger.debug('Push notification error on registering', e)
            })
        // Only get delivered notifications after successful registration
        //await getDeliveredNotifications()
        Logger.debug('Push notifications setup completed')
      } catch (e) {
        Logger.error('Error registering notifications', e)
      }
    }
  }, 1000);
})

onBeforeUnmount(() => {
  notifications.closeMercureSubscription();
})

onBeforeMount(() => {
  checkForcedLayout(useRoute())

  setTimeout(() => {
    loading.value = false
  }, 500)
})

</script>
